import React, { useContext, useCallback, useState } from 'react';
import { Dialog, DialogContent, DialogActions, Button, DialogTitle, DialogContentText, makeStyles, Theme, TextField } from '@material-ui/core';
import { DateTimePicker } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import clsx from 'clsx';
import { useModeFormStyle, useGenericDialogStyle, useScreenSizeCheck, capitalizeAll } from '@moderepo/mode-app-components';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { useRitzTranslation } from '../../utils/customHooks';
import { LanguageSelectorContainer } from '../../containers/language-selector/LanguageSelectorContainer';


const useStyle = makeStyles((theme: Theme) => {
    return {
        form: {
            '& > *': {
                width: '100%',
            },
        },

        timeZone: {
        },

        datePickersContainer: {
            display      : 'flex',
            alignItems   : 'center',
            flexDirection: 'column',
        },

        datePicker: {
            marginBottom: theme.spacing(1),
        },

        datePickerInput: {
            '&.hidden': {
                visibility : 'hidden',
                '&::before': {
                    content: '""',
                },
            },
        },

        errorMessage: {
            color    : theme.palette.error.main,
            alignSelf: 'flex-start',
        },

        [theme.breakpoints.up('md')]: {
            datePickersContainer: {
                flexDirection : 'row',
                justifyContent: 'space-between',
            },
            datePicker: {
                marginBottom   : theme.spacing(0),
                '&:first-child': {
                    marginRight: theme.spacing(1),
                },
                '&:last-child': {
                    marginLeft: theme.spacing(1),
                },
            },
        },
    };
}, { name: 'SelectDateRangeDialog' });


const DEFAULT_DATE_TIME_FORMAT = 'YYYY/MM/DD HH:mm:00';


export interface SelectDateRangeDialogProps {
    readonly projectId: number | undefined;
    readonly open: boolean;
    readonly titleKey: string;
    readonly subtitleKey?: string | undefined;

    // Time Zone will only be used for displaying to the user, not an option for the user to select.
    readonly displayedTimeZone?: string | undefined;

    // The format of the displayed date/time input field. If this is not provided, the default format will be 'YYYY/MM/DD HH:mm:00'
    readonly dateFormat?: string | undefined;

    readonly onCancel: ()=> void;
    readonly onDateRangeSelected: (startDate: Date, endDate: Date)=> void;
}



/**
 * This is a dialog used for creating a request to get a device's log
 */
export const SelectDateRangeDialog: React.FC<SelectDateRangeDialogProps> = (
    props: SelectDateRangeDialogProps,
) => {

    const { state:dataState } = useContext(ritzDataContext);
    const project = props.projectId ? selectProjectById(dataState, props.projectId) : undefined;
    const { trans } = useRitzTranslation(project);

    const formClasses = useModeFormStyle();
    const dialogClasses = useGenericDialogStyle();
    const classes = useStyle();

    const isMediumScreen = useScreenSizeCheck('md');
    const [startDate, setStartDate] = useState<MaterialUiPickersDate | undefined>(undefined);
    const [endDate, setEndDate] = useState<MaterialUiPickersDate | undefined>(undefined);

    // Callback Handlers
    const onCancelHandler = props.onCancel;
    const onDateRangeSelected = props.onDateRangeSelected;


    /**
     * On Close button click, call the onCancel handler
     */
    const onCloseButtonClicked = useCallback(() => {
        setStartDate(undefined);
        setEndDate(undefined);
        onCancelHandler();
    }, [onCancelHandler]);


    /**
     * On form submit. All input are optional so we don't need to validate them.
     */
    const onFormSubmit = useCallback(async (event: React.FormEvent) => {
        event.stopPropagation();
        event.preventDefault();

        if (startDate && endDate) {
            onDateRangeSelected(startDate.seconds(0).milliseconds(0).toDate(), endDate.seconds(0).milliseconds(0).toDate());
        }

        return false;
    }, [startDate, endDate, onDateRangeSelected]);


    return (
        <Dialog
            open={props.open}
            fullScreen={!isMediumScreen}
            fullWidth={isMediumScreen}
            disableEscapeKeyDown={false}
            onClose={(event, reason) => {
                // Don't close the dialog when backdrop clicked
                if (reason !== 'backdropClick') {
                    setStartDate(undefined);
                    props.onCancel();
                }
            }}
        >
            <form className={clsx(formClasses.root, classes.form)} noValidate autoComplete="on" method="POST" onSubmit={onFormSubmit}>
                <DialogTitle className={dialogClasses.dialogTitle}>
                    <div className={dialogClasses.dialogTitleText}>
                        {capitalizeAll(trans(props.titleKey))}
                    </div>
                    <LanguageSelectorContainer projectId={props.projectId} minimal />
                </DialogTitle>

                {props.subtitleKey && (
                    <DialogContent
                        className={dialogClasses.dialogContent}
                    >
                        <DialogContentText className={dialogClasses.dialogMessage}>
                            {trans(props.subtitleKey)}
                        </DialogContentText>
                    </DialogContent>
                )}

                <DialogContent
                    className={dialogClasses.dialogContent}
                >
                    {props.displayedTimeZone && (
                        <TextField
                            className={clsx(formClasses.formInputField, classes.timeZone)}
                            variant="standard"
                            type="text"
                            label={capitalizeAll(trans('label.time_zone'))}
                            value={props.displayedTimeZone}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            InputProps={{
                                readOnly: true,
                                disabled: true,
                            }}
                        />
                    )}

                    <div className={clsx(formClasses.formInputField, classes.datePickersContainer)}>
                        <DateTimePicker
                            className={clsx(classes.datePicker)}
                            fullWidth
                            format={props.dateFormat ?? DEFAULT_DATE_TIME_FORMAT}
                            ampm={false}
                            label={capitalizeAll(trans('label.start_date'))}
                            todayLabel={capitalizeAll(trans('label.today'))}
                            okLabel={capitalizeAll(trans('label.ok'))}
                            cancelLabel={capitalizeAll(trans('label.cancel'))}
                            clearLabel={capitalizeAll(trans('label.clear'))}
                            invalidDateMessage=""
                            maxDateMessage=""
                            minDateMessage=""
                            animateYearScrolling
                            showTodayButton
                            clearable
                            autoOk
                            value={startDate}
                            maxDate={endDate}
                            inputProps={{
                                className: `${classes.datePickerInput} ${startDate === undefined ? 'hidden' : ''}`,
                            }}
                            onChange={(date: MaterialUiPickersDate) => {
                                // Since the controller doesn't let the user pick the seconds/milliseconds, we need to remove seconds and milliseconds
                                setStartDate(date?.seconds(0).milliseconds(0));
                            }}
                        />
                        <DateTimePicker
                            className={clsx(classes.datePicker)}
                            fullWidth
                            format={props.dateFormat ?? DEFAULT_DATE_TIME_FORMAT}
                            ampm={false}
                            label={capitalizeAll(trans('label.end_date'))}
                            todayLabel={capitalizeAll(trans('label.today'))}
                            okLabel={capitalizeAll(trans('label.ok'))}
                            cancelLabel={capitalizeAll(trans('label.cancel'))}
                            clearLabel={capitalizeAll(trans('label.clear'))}
                            invalidDateMessage=""
                            maxDateMessage=""
                            minDateMessage=""
                            animateYearScrolling
                            showTodayButton
                            clearable
                            autoOk
                            value={endDate}
                            minDate={startDate}
                            inputProps={{
                                className: `${classes.datePickerInput} ${endDate === undefined ? 'hidden' : ''}`,
                            }}
                            onChange={(date: MaterialUiPickersDate) => {
                                // Since the controller doesn't let the user pick the seconds/milliseconds, we need to remove seconds and milliseconds
                                setEndDate(date?.seconds(0).milliseconds(0));
                            }}
                        />
                    </div>
                    {startDate && endDate && !startDate.isBefore(endDate) && (
                        <div className={classes.errorMessage}>
                            {trans('error.input.date_order.invalid')}
                        </div>
                    )}
                </DialogContent>

                <DialogActions
                    className={dialogClasses.dialogActionContainer}
                >
                    <Button
                        onClick={onCloseButtonClicked}
                    >
                        {trans('label.cancel')}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={!(startDate && endDate) || !startDate.isBefore(endDate)}
                    >
                        {trans('label.ok')}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};
